import React, {useEffect, useRef, useState} from 'react';
import {FiTrash2, FiX} from 'react-icons/fi';
import './GalleryModal.css';
import AttachmentService from '../../services/AttachmentService';
import AuthImage from '../authImage/AuthImage';
import ImageViewer from '../imageViewer/ImageViewer'; // Import the new ImageViewer

function GalleryModal({isOpen, onClose, initialImages = [], customerId, onPendingImages}) {
    const [activeTab, setActiveTab] = useState('imagini');
    const [photoAttachments, setPhotoAttachments] = useState([]);
    const [projectAttachments, setProjectAttachments] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [imageViewerOpen, setImageViewerOpen] = useState(false);
    const [viewerImages, setViewerImages] = useState([]);
    const [viewerInitialIndex, setViewerInitialIndex] = useState(0);
    const fileInputRef = useRef(null);

    // Initialize modal state when opened
    useEffect(() => {
        if (isOpen) {
            // If we have a customer ID, fetch attachments
            if (customerId) {
                fetchAttachments();
            }
        }
    }, [isOpen, customerId, initialImages]);

    // Fetch attachments from server
    const fetchAttachments = async () => {
        if (!customerId) return;

        try {
            setIsLoading(true);

            // Fetch photo attachments
            const photos = await AttachmentService.getAttachments(customerId, 'PHOTO');
            setPhotoAttachments(photos);

            // Fetch project (drawing) attachments
            const projects = await AttachmentService.getAttachments(customerId, 'PROJECT');
            setProjectAttachments(projects);

            console.log("Fetched attachments:", {photos, projects});
        } catch (error) {
            console.error('Error fetching attachments:', error);
        } finally {
            setIsLoading(false);
        }
    };

    if (!isOpen) return null;

    const handleTabChange = (tab) => {
        setActiveTab(tab);
    };

    const handleImageClick = (image, index) => {
        // Don't open viewer for uploading images
        if (image.isUploading) return;

        // Get the current tab's images (exclude uploading ones)
        const currentImages = activeTab === 'proiecte'
            ? projectAttachments.filter(img => !img.isUploading)
            : photoAttachments.filter(img => !img.isUploading);

        // Find the correct index excluding uploading images
        const viewerIndex = currentImages.findIndex(img => img.id === image.id);

        // Open the image viewer with all images from current tab
        setViewerImages(currentImages);
        setViewerInitialIndex(viewerIndex >= 0 ? viewerIndex : 0);
        setImageViewerOpen(true);
    };

    const handleDeleteImage = async (e, image) => {
        e.stopPropagation(); // Prevent triggering onClick of parent container

        // Don't delete images that are still uploading
        if (image.isUploading) return;

        // Remove from UI immediately
        const setAttachments = activeTab === 'imagini' ? setPhotoAttachments : setProjectAttachments;
        setAttachments(prev => {
            const updated = prev.filter(img => img.id !== image.id);
            // For new clients, notify parent of remaining pending images
            if (!customerId && onPendingImages) {
                const allPending = activeTab === 'imagini'
                    ? [...updated.filter(img => img.file), ...projectAttachments.filter(img => img.file)]
                    : [...photoAttachments.filter(img => img.file), ...updated.filter(img => img.file)];
                onPendingImages(allPending);
            }
            return updated;
        });

        // Delete from server if it has an attachId
        if (image.attachId && customerId) {
            try {
                await AttachmentService.deleteAttachment(image.attachId);
                AttachmentService.clearCache(customerId);
            } catch (err) {
                console.error('Error deleting attachment:', err);
            }
        }
    };

    const handleFileChange = async (e) => {
        const files = Array.from(e.target.files);
        if (files.length === 0) return;

        // Reset the file input
        e.target.value = '';

        const currentTab = activeTab;
        const attachType = currentTab === 'imagini' ? 'photo' : 'project';
        const setAttachments = currentTab === 'imagini' ? setPhotoAttachments : setProjectAttachments;

        if (customerId) {
            // Auto-upload for existing clients
            const previews = files.map(file => ({
                id: 'temp-' + Date.now() + Math.random(),
                url: URL.createObjectURL(file),
                fileName: file.name,
                attachType: attachType.toUpperCase(),
                isUploading: true
            }));

            setAttachments(prev => [...prev, ...previews]);
            setIsUploading(true);

            try {
                for (const file of files) {
                    await AttachmentService.createAttachment({
                        customerId,
                        files: file,
                        attachType
                    });
                }

                // Refetch attachments to get proper server data with URLs
                AttachmentService.clearCache(customerId);
                const photos = await AttachmentService.getAttachments(customerId, 'PHOTO', true);
                const projects = await AttachmentService.getAttachments(customerId, 'PROJECT', true);
                setPhotoAttachments(photos);
                setProjectAttachments(projects);
            } catch (err) {
                console.error('Error uploading files:', err);
                setAttachments(prev => prev.filter(img => !img.isUploading));
            } finally {
                setIsUploading(false);
            }
        } else {
            // Store locally for new clients (no customerId yet)
            const newImagePreviews = files.map(file => ({
                id: Date.now() + Math.random(),
                url: URL.createObjectURL(file),
                file: file,
                fileName: file.name,
                attachType: attachType.toUpperCase()
            }));

            setAttachments(prev => {
                const updated = [...prev, ...newImagePreviews];
                // Notify parent of all pending images (photos + projects)
                if (onPendingImages) {
                    const allPending = currentTab === 'imagini'
                        ? [...updated.filter(img => img.file), ...projectAttachments.filter(img => img.file)]
                        : [...photoAttachments.filter(img => img.file), ...updated.filter(img => img.file)];
                    onPendingImages(allPending);
                }
                return updated;
            });
        }
    };

    const handleUploadClick = () => {
        fileInputRef.current.click();
    };

    // Combine images according to active tab
    const currentAttachments = activeTab === 'proiecte' ? projectAttachments : photoAttachments;

    return (
        <div className="gallery-modal-overlay">
            <div className="gallery-modal-content">
                <button className="gallery-modal-close" onClick={onClose}>
                    <FiX/>
                </button>

                <div className="gallery-modal-tabs">
                    <button
                        className={`gallery-tab ${activeTab === 'proiecte' ? 'active' : ''}`}
                        onClick={() => handleTabChange('proiecte')}
                    >
                        Proiecte
                    </button>
                    <button
                        className={`gallery-tab ${activeTab === 'imagini' ? 'active' : ''}`}
                        onClick={() => handleTabChange('imagini')}
                    >
                        Imagini mobilă
                    </button>
                </div>

                <div className="gallery-modal-body">
                    {isLoading ? (
                        <div className="gallery-loading">Se încarcă imaginile...</div>
                    ) : (
                        <div className="gallery-grid">
                            {currentAttachments.map((image, index) => (
                                <div
                                    key={image.id}
                                    className={`gallery-item ${image.isUploading ? 'uploading' : ''}`}
                                    onClick={() => handleImageClick(image, index)}
                                >
                                    {/* Use the AuthImage component instead of the standard img tag */}
                                    <AuthImage
                                        src={image.url}
                                        alt={image.fileName || "Gallery item"}
                                    />
                                    {!image.isUploading && (
                                        <button
                                            className="delete-image-btn"
                                            onClick={(e) => handleDeleteImage(e, image)}
                                            aria-label="Delete image"
                                        >
                                            <FiTrash2/>
                                        </button>
                                    )}
                                </div>
                            ))}
                            {/* Add photo button as grid tile */}
                            <div
                                className={`gallery-item gallery-add-item ${isUploading ? 'disabled' : ''}`}
                                onClick={!isUploading ? handleUploadClick : undefined}
                            >
                                <span className="gallery-add-icon">+</span>
                            </div>
                        </div>
                    )}
                </div>

                {/* Hidden file input */}
                <input
                    type="file"
                    ref={fileInputRef}
                    style={{display: 'none'}}
                    multiple
                    accept="image/*"
                    onChange={handleFileChange}
                />
            </div>
            <ImageViewer
                isOpen={imageViewerOpen}
                onClose={() => setImageViewerOpen(false)}
                images={viewerImages}
                initialIndex={viewerInitialIndex}
            />
        </div>
    );
}

export default GalleryModal;
